<template>
  <div class="scoring-profile-filters-panel">
    <selected-filters></selected-filters>
    <hr class="my-2" />

    <div v-for="(group, index) in groups" :key="group.groupKey">
      <filter-accordion-section
        :title="groupLabel(group.groupKey)"
        :start-open="false"
        :last="index === groups.length - 1 && otherUngrouped.length === 0"
      >
        <div v-for="filter in group.filters" :key="filter.filterKey" class="fsb-field">
          <div>
            <label v-if="filter.filterType !== 'RangeDate'" class="fsb-label">{{ filterLabel(filter.filterKey) }}</label>
            <div class="fsb-select-wrap">
              <filter-field
                :filter="filter"
                :value="filterValue(filter.filterKey)"
                :perimeter-placeholder="$t('search.perimeterModes.most_recent')"
                :hide-in-perimeter="true"
                :label="filter.filterType === 'RangeDate' ? filterLabel(filter.filterKey) : null"
                @input="(value) => onFilterInput(filter.filterKey, value)"
              ></filter-field>
            </div>
          </div>
        </div>
      </filter-accordion-section>
    </div>

    <filter-accordion-section v-if="otherUngrouped.length > 0" :title="$t('search.ungrouped')" :start-open="false" :last="true">
      <div v-for="filter in otherUngrouped" :key="filter.filterKey" class="fsb-field">
        <div>
          <label v-if="filter.filterType !== 'RangeDate'" class="fsb-label">{{ filterLabel(filter.filterKey) }}</label>
          <div class="fsb-select-wrap">
            <filter-field
              :filter="filter"
              :value="filterValue(filter.filterKey)"
              :perimeter-placeholder="$t('search.perimeterModes.most_recent')"
              :label="filter.filterType === 'RangeDate' ? filterLabel(filter.filterKey) : null"
              @input="(value) => onFilterInput(filter.filterKey, value)"
            ></filter-field>
          </div>
        </div>
      </div>
    </filter-accordion-section>
  </div>
</template>

<script>
// Catalog-driven filter authoring for the scoring profile create/edit flow. Uses the SAME generic
// widget layer as search (FilterField.vue + filterTypes/*.vue) but with a much lighter
// orchestration: no draft/apply/live-search pattern, no
// pagination — filter values are bound directly to the `scoringProfilesFilters` Vuex module and
// read out (via `filtersPayload`) at Save time by the page component.
//
// Coupled directly to the Vuex module rather than exposing props/emits, matching the established
// pattern in this area of the app.
import { createNamespacedHelpers } from 'vuex'
import FilterField from '../Search/FilterField.vue'
import FilterAccordionSection from '../Search/FilterAccordionSection.vue'
import SelectedFilters from './SelectedFilters.vue'

const { mapState, mapGetters, mapActions } = createNamespacedHelpers('scoringProfilesFilters')
const { mapActions: mapCommonActions } = createNamespacedHelpers('scoringProfileEditor')

export default {
  name: 'scoringProfileFiltersPanel',
  components: { FilterField, FilterAccordionSection, SelectedFilters },
  computed: {
    ...mapState({
      catalog: (state) => state.catalog,
      filters: (state) => state.filters,
    }),
    ...mapGetters(['filterValue']),
    groups() {
      return this.catalog.groups || []
    },
    otherUngrouped() {
      // SmartFilter isn't a compatibility filter and shouldn't appear in the catalog response for
      // scoring profiles, but skip it defensively if present.
      return (this.catalog.ungrouped || []).filter((f) => f.filterKey !== 'SmartFilter')
    },
  },
  methods: {
    ...mapActions(['loadFiltersCatalog', 'setFilterValue']),
    ...mapCommonActions(['loadProfileCoverage']),
    onFilterInput(key, value) {
      this.setFilterValue({ key, value })
    },
    groupLabel(groupKey) {
      const key = 'search.groups.' + groupKey
      return this.$te(key) ? this.$t(key) : groupKey
    },
    filterLabel(filterKey) {
      const key = 'search.filters.' + filterKey
      return this.$te(key) ? this.$t(key) : filterKey
    },
  },
  watch: {
    // Deep watch on the catalog filters array, which drives the "Copertura profilo" live preview.
    filters: {
      handler() {
        this.loadProfileCoverage()
      },
      deep: true,
    },
  },
  mounted() {
    if (!this.catalog.groups.length && !this.catalog.ungrouped.length) {
      this.loadFiltersCatalog()
    }
    // Deliberately NOT calling loadProfileCoverage() here — this component mounts synchronously before
    // scoringProfileEditor/loadScoringProfile has had a chance to set
    // isProfileCoverageProvidedByAPI (dispatched asynchronously, after
    // loadModelsListToAssociate resolves, from the page's created() hook). Calling it here fired a
    // spurious POST /api/scoringProfiles/coverage with empty filters on every entry into both
    // create and edit mode, before the guard was ever engaged and before real/default filters were
    // hydrated. The watch.filters handler above covers it:
    // blocked by the guard while a profile's filters are being loaded, and fires once (in create
    // mode) when default filters are actually assigned after this component is already mounted.
  },
}
</script>
